// Intent: render a full legal document (regulamin / polityka prywatności) inside
// the app — the user has no WordPress, so the terms live here. Lightweight
// markdown-lite renderer (headings, notes, bullets, numbered items, tables) over
// the bundled text in src/content/legal-docs.ts. No markdown dependency.

import { useLocalSearchParams } from 'expo-router';
import { ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { ScreenHeader } from '@/components/ui/screen-header';
import { LEGAL_DOCS, type LegalDoc } from '@/content/legal-docs';
import { colors, radius, spacing } from '@/theme/tokens';

const strip = (s: string) => s.replace(/\*\*/g, '').trim();

/** Group the raw lines into renderable blocks. Tables are collected as row arrays. */
type Block =
  | { type: 'h2' | 'h3' | 'note' | 'p'; text: string }
  | { type: 'bullet' | 'num'; text: string; sub?: boolean }
  | { type: 'table'; rows: string[][] };

function parse(body: string): Block[] {
  const lines = body.split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();
    if (line === '') {
      i++;
      continue;
    }
    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i].trim().replace(/^\||\|$/g, '').split('|').map((c) => strip(c));
        // skip the |---|---| separator row
        if (!cells.every((c) => /^:?-+:?$/.test(c.replace(/\s/g, '')) || c === '')) {
          rows.push(cells);
        }
        i++;
      }
      if (rows.length) blocks.push({ type: 'table', rows });
      continue;
    }
    if (line.startsWith('### ')) blocks.push({ type: 'h3', text: strip(line.slice(4)) });
    else if (line.startsWith('## ')) blocks.push({ type: 'h2', text: strip(line.slice(3)) });
    else if (line.startsWith('> ')) blocks.push({ type: 'note', text: strip(line.slice(2)) });
    else if (/^\d+\.\s/.test(line)) blocks.push({ type: 'num', text: strip(line.replace(/^\d+\.\s/, '')) });
    else if (line.startsWith('- ')) blocks.push({ type: 'bullet', text: strip(line.slice(2)), sub: /^\s{2,}-/.test(raw) });
    else blocks.push({ type: 'p', text: strip(line) });
    i++;
  }
  return blocks;
}

export default function LegalDocScreen() {
  const { doc } = useLocalSearchParams<{ doc: string }>();
  const key = doc === 'polityka' ? 'polityka' : 'regulamin';
  const document: LegalDoc = LEGAL_DOCS[key];
  const blocks = parse(document.body);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.sm, paddingBottom: spacing.xl }}>
        <ScreenHeader title={document.title} />
        <AppText variant="labelMd" color={colors.outline}>
          Aktualizacja: {document.updated}
          {document.draft ? ' · wersja robocza (w konsultacji prawnej)' : ''}
        </AppText>

        {blocks.map((b, idx) => {
          if (b.type === 'h2')
            return (
              <AppText key={idx} variant="headlineSm" color={colors.navy} style={{ marginTop: spacing.md }}>
                {b.text}
              </AppText>
            );
          if (b.type === 'h3')
            return (
              <AppText key={idx} variant="labelLg" color={colors.navy} style={{ marginTop: spacing.sm }}>
                {b.text}
              </AppText>
            );
          if (b.type === 'note')
            return (
              <View
                key={idx}
                style={{ backgroundColor: colors.infoBg, borderRadius: radius.card, padding: spacing.md }}
              >
                <AppText variant="bodyMd">{b.text}</AppText>
              </View>
            );
          if (b.type === 'bullet' || b.type === 'num')
            return (
              <View
                key={idx}
                style={{ flexDirection: 'row', gap: spacing.xs, paddingLeft: b.sub ? spacing.lg : spacing.sm }}
              >
                <AppText variant="bodyMd" color={colors.primary}>
                  {b.type === 'num' ? '•' : '–'}
                </AppText>
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {b.text}
                </AppText>
              </View>
            );
          if (b.type === 'table') {
            const [head, ...rest] = b.rows;
            return (
              <View key={idx} style={{ gap: spacing.xs }}>
                {rest.map((row, ri) => (
                  <View
                    key={ri}
                    style={{
                      backgroundColor: colors.card,
                      borderRadius: radius.card,
                      padding: spacing.md,
                      gap: 4,
                      borderWidth: 1,
                      borderColor: colors.divider,
                    }}
                  >
                    {row.map((cell, ci) => (
                      <View key={ci} style={{ flexDirection: 'row', gap: spacing.xs }}>
                        {head?.[ci] ? (
                          <AppText variant="labelMd" color={colors.outline} style={{ width: 110 }}>
                            {head[ci]}
                          </AppText>
                        ) : null}
                        <AppText variant="bodyMd" style={{ flex: 1 }}>
                          {cell}
                        </AppText>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            );
          }
          return (
            <AppText key={idx} variant="bodyMd">
              {b.text}
            </AppText>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}
